import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { noteApi, fileApi } from '../utils/api';
import { useTranslation } from 'react-i18next';
import {
    Card,
    CardBody,
    Form,
    FormGroup,
    Label,
    Input,
    Button,
    ListGroup,
    ListGroupItem,
    Badge,
    Spinner,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter
} from 'reactstrap';
import {
    ArrowLeft,
    Trash2,
    Eye,
    Download,
    Paperclip,
    Upload,
    Save,
    FileText,
    CheckCircle2,
    Clock,
    X,
    Copy,
    Check,
    Lock,
    EyeOff
} from 'lucide-react';

import DateTimePicker from '../components/DateTimePicker';

const EditNote = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { t } = useTranslation();
    
    const [note, setNote] = useState({ title: '', content: '', tags: [], attachments: [], targetDate: '', completed: false, confidential: false });
    const [isContentRevealed, setIsContentRevealed] = useState(false);
    const [newFiles, setNewFiles] = useState([]);
    const [tagInput, setTagInput] = useState('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    // Preview state (includes id and extractedText so preview modal can show & copy extracted text)
    const [preview, setPreview] = useState({
        isOpen: false,
        id: null,
        url: null,
        type: null,
        fileName: '',
        textContent: null,
        extractedText: null,
        showExtractedText: false
    });
    const [loadingPreviewId, setLoadingPreviewId] = useState(null);
    const [copiedExtracted, setCopiedExtracted] = useState(false);

    useEffect(() => {
        setIsContentRevealed(false);
        const fetchNote = async () => {
            try {
                const res = await noteApi.get(id);
                const data = res.data;
                setNote({
                    title: data.title || '',
                    content: data.content || '',
                    tags: data.tags || [],
                    attachments: data.attachments || [],
                    targetDate: data.targetDate ? data.targetDate.substring(0, 16) : '',
                    completed: data.completed || false,
                    confidential: data.confidential || false,
                    createdAt: data.createdAt,
                    updatedAt: data.updatedAt
                });
            } catch (err) {
                console.error("Failed to fetch note", err);
            } finally {
                setLoading(false);
            }
        };
        fetchNote();
    }, [id]);

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const noteToSave = { 
                ...note, 
                targetDate: note.targetDate ? note.targetDate : null 
            };
            await noteApi.put(id, noteToSave);
            if (newFiles.length > 0) {
                for (const file of newFiles) {
                    const formData = new FormData();
                    formData.append('file', file);
                    await noteApi.post(`${id}/attachments`, formData);
                }
            }
            // Notify other components (like Calendar) that data changed
            window.dispatchEvent(new CustomEvent('notes-updated'));
            navigate(-1);
        } catch (err) {
            console.error("Update failed", err);
            alert(t('error_saving'));
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (window.confirm(t('delete_note') + "?")) {
            try {
                await noteApi.delete(id);
                navigate('/dashboard', { state: { deleted: true } });
            } catch (err) {
                console.error("Delete failed", err);
            }
        }
    };

    const handleAddTag = (e) => {
        if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault();
            commitTag();
        }
    };

    const commitTag = () => {
        const tag = tagInput.trim().replace(',', '');
        if (tag && !note.tags.includes(tag)) {
            setNote(prev => ({ ...prev, tags: [...prev.tags, tag] }));
        }
        setTagInput('');
    };

    const removeTag = (tagToRemove) => {
        setNote({ ...note, tags: note.tags.filter(t => t !== tagToRemove) });
    };

    const handleFileChange = (e) => {
        setNewFiles(prev => [...prev, ...Array.from(e.target.files)]);
    };

    const removeNewFile = (index) => {
        setNewFiles(newFiles.filter((_, i) => i !== index));
    };

    const handleDownload = async (attachmentId, fileName, existingBlobUrl = null) => {
        try {
            if (existingBlobUrl) {
                const link = document.createElement('a');
                link.href = existingBlobUrl;
                link.setAttribute('download', fileName || 'attachment');
                document.body.appendChild(link);
                link.click();
                link.parentNode.removeChild(link);
                return;
            }
            const targetId = attachmentId || preview.id;
            if (!targetId) return;
            const response = await fileApi.get(targetId, { responseType: 'blob' });
            const url = window.URL.createObjectURL(response.data);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', fileName || 'attachment');
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error("Download failed", err);
        }
    };

    const handlePreview = async (attachmentId, fileName, attachmentExtractedText = null) => {
        if (loadingPreviewId) return;
        setLoadingPreviewId(attachmentId);
        setCopiedExtracted(false);
        try {
            const response = await fileApi.get(attachmentId, { responseType: 'blob' });
            const fileType = response.data.type || 'application/octet-stream';
            const url = window.URL.createObjectURL(response.data);
            
            let textContent = null;
            if (fileType.startsWith('text/')) {
                textContent = await response.data.text();
            }

            const matchedAttachment = note.attachments?.find(a => a.id === attachmentId);
            const rawExtracted = attachmentExtractedText ?? matchedAttachment?.extractedText ?? textContent ?? null;
            const resolvedExtractedText = (typeof rawExtracted === 'string' && rawExtracted.trim().length > 0)
                ? rawExtracted.trim()
                : null;

            setPreview({
                isOpen: true,
                id: attachmentId,
                url: url,
                type: fileType,
                fileName: fileName,
                textContent: textContent,
                extractedText: resolvedExtractedText,
                showExtractedText: false
            });
        } catch (err) {
            console.error("Preview failed", err);
            alert(t('error_preview'));
        } finally {
            setLoadingPreviewId(null);
        }
    };

    const handleCopyExtractedText = async () => {
        if (!preview.extractedText) return;
        try {
            await navigator.clipboard.writeText(preview.extractedText);
            setCopiedExtracted(true);
            setTimeout(() => setCopiedExtracted(false), 2000);
        } catch (err) {
            console.error("Failed to copy extracted text", err);
        }
    };

    const closePreview = () => {
        if (preview.url) window.URL.revokeObjectURL(preview.url);
        setCopiedExtracted(false);
        setPreview({
            isOpen: false,
            id: null,
            url: null,
            type: null,
            fileName: '',
            textContent: null,
            extractedText: null,
            showExtractedText: false
        });
    };

    const deleteAttachment = async (attachmentId) => {
        if (window.confirm(t('confirm_delete_attachment'))) {
            try {
                await noteApi.delete(`${id}/attachments/${attachmentId}`);
                setNote({ ...note, attachments: note.attachments.filter(a => a.id !== attachmentId) });
            } catch (err) {
                console.error("Failed to delete attachment", err);
            }
        }
    };

    if (loading) return <div className="text-center mt-5"><Spinner color="primary" /></div>;

    const renderPreviewContent = () => {
        if (!preview.url) return null;

        if (preview.type.startsWith('image/')) {
            return (
                <div className="text-center">
                    <img src={preview.url} alt={preview.fileName} className="img-fluid rounded shadow-sm" style={{ maxHeight: '70vh' }} />
                </div>
            );
        }
        
        if (preview.type === 'application/pdf') {
            return <iframe src={preview.url} title={preview.fileName} width="100%" height="600px" className="border-0 rounded" />;
        }

        if (preview.type.startsWith('text/') && preview.textContent !== null) {
            return (
                <div className="bg-custom p-3 rounded border overflow-auto" style={{ maxHeight: '600px', backgroundColor: 'var(--sb-bg)' }}>
                    <pre className="mb-0" style={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '14px', color: 'var(--sb-text)' }}>
                        {preview.textContent}
                    </pre>
                </div>
            );
        }

        return (
            <div className="text-center py-5">
                <div className="mb-3 text-primary">
                    <FileText size={56} />
                </div>
                <p className="opacity-75 mb-4">{t('preview_not_available')} (<strong>{preview.type}</strong>)</p>
                <Button
                    color="primary"
                    className="rounded-pill px-4 py-2 fw-bold shadow-sm d-inline-flex align-items-center gap-2"
                    onClick={() => handleDownload(preview.id, preview.fileName, preview.url)}
                >
                    <Download size={18} />
                    <span>{t('download_to_view')}</span>
                </Button>
            </div>
        );
    };

    return (
        <div className="container mt-4 pb-5 animate-fade-in" style={{ maxWidth: '900px' }}>
            <div className="d-flex flex-nowrap justify-content-between align-items-center gap-2 mb-4">
                <Button
                    color="light"
                    onClick={() => navigate(-1)}
                    className="rounded-pill px-2 px-sm-3 py-2 border shadow-sm small btn-theme-aware d-inline-flex align-items-center justify-content-center gap-2 fw-semibold flex-shrink-0"
                    title={t('back_button')}
                >
                    <ArrowLeft size={16} />
                    <span className="d-none d-sm-inline">{t('back_button')}</span>
                </Button>
                <h2 className="fw-bold mb-0 fs-5 fs-md-3 text-truncate">{t('edit_note_title')}</h2>
                <Button
                    color="danger"
                    outline
                    size="sm"
                    onClick={handleDelete}
                    className="rounded-pill px-2 px-sm-3 py-2 shadow-sm d-inline-flex align-items-center justify-content-center gap-1 fw-semibold flex-shrink-0"
                    title={t('delete_note')}
                >
                    <Trash2 size={16} />
                    <span className="d-none d-sm-inline">{t('delete_note')}</span>
                </Button>
            </div>

            <Card className="border-0 shadow-sm rounded-4 bg-card-custom no-hover-card">
                <CardBody className="p-3 p-sm-4 p-md-5">
                    <div className="d-flex flex-wrap gap-3 mb-4 opacity-75 small border-bottom border-custom pb-3">
                        <span className="d-inline-flex align-items-center gap-1">
                            <Clock size={14} />
                            <strong>{t('created_at')}:</strong> {note.createdAt ? new Date(note.createdAt).toLocaleString() : ''}
                        </span>
                        <span className="d-inline-flex align-items-center gap-1">
                            <Clock size={14} />
                            <strong>{t('updated_at')}:</strong> {note.updatedAt ? new Date(note.updatedAt).toLocaleString() : ''}
                        </span>
                    </div>
                    <Form onSubmit={handleSave}>
                        <FormGroup className="mb-4">
                            <Label className="small fw-bold opacity-75 text-uppercase mb-2 d-block">{t('title_placeholder')}</Label>
                            <Input 
                                type="text" 
                                className="form-control-lg border-custom bg-custom rounded-3 fw-bold"
                                value={note.title} 
                                onChange={e => setNote({ ...note, title: e.target.value })} 
                                placeholder={t('title_input_placeholder')}
                            />
                        </FormGroup>

                        <FormGroup className="mb-4">
                            <div className="p-3 rounded-3 border border-custom bg-custom d-flex flex-wrap justify-content-between align-items-center gap-2">
                                <div className="d-flex flex-column">
                                    <Label className="form-check-label fw-bold mb-1 d-inline-flex align-items-center gap-2 text-warning" htmlFor="editNoteConfidentialSwitch" style={{ cursor: 'pointer' }}>
                                        <Lock size={16} />
                                        <span>{t('mark_as_confidential')}</span>
                                    </Label>
                                    <small className="text-muted">{t('confidential_hint')}</small>
                                </div>
                                <div className="form-check form-switch mb-0">
                                    <Input
                                        type="checkbox"
                                        className="form-check-input"
                                        role="switch"
                                        id="editNoteConfidentialSwitch"
                                        style={{ cursor: 'pointer', width: '2.5em', height: '1.25em' }}
                                        checked={note.confidential}
                                        onChange={e => setNote({ ...note, confidential: e.target.checked })}
                                    />
                                </div>
                            </div>
                        </FormGroup>

                        <FormGroup className="mb-4">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <Label className="small fw-bold opacity-75 text-uppercase mb-0">{t('content_placeholder')}</Label>
                                {note.confidential && isContentRevealed && (
                                    <Button
                                        color="warning"
                                        outline
                                        size="sm"
                                        type="button"
                                        className="rounded-pill px-3 py-1 fw-semibold d-inline-flex align-items-center gap-1"
                                        onClick={() => setIsContentRevealed(false)}
                                    >
                                        <EyeOff size={14} />
                                        <span>{t('hide_confidential_content')}</span>
                                    </Button>
                                )}
                            </div>
                            {note.confidential && !isContentRevealed ? (
                                <div className="p-5 rounded-3 border border-custom bg-custom text-center d-flex flex-column align-items-center justify-content-center gap-3" style={{ minHeight: '220px' }}>
                                    <div className="p-3 rounded-circle bg-warning bg-opacity-10 text-warning d-inline-flex">
                                        <Lock size={32} />
                                    </div>
                                    <div>
                                        <h6 className="fw-bold mb-1">{t('confidential_content_hidden')}</h6>
                                        <p className="text-muted small mb-0">{t('confidential_content_hidden_desc')}</p>
                                    </div>
                                    <Button
                                        color="warning"
                                        type="button"
                                        className="rounded-pill px-4 py-2 fw-bold shadow-sm d-inline-flex align-items-center gap-2"
                                        onClick={() => setIsContentRevealed(true)}
                                    >
                                        <Eye size={16} />
                                        <span>{t('show_confidential_content')}</span>
                                    </Button>
                                </div>
                            ) : (
                                <Input 
                                    type="textarea" 
                                    className="border-custom bg-custom rounded-3"
                                    style={{ minHeight: '300px', resize: 'vertical' }}
                                    value={note.content} 
                                    onChange={e => setNote({ ...note, content: e.target.value })} 
                                    placeholder={t('content_input_placeholder')}
                                />
                            )}
                        </FormGroup>

                        <FormGroup className="mb-4">
                            <Label className="small fw-bold opacity-75 text-uppercase mb-2 d-block">{t('tags_placeholder')}</Label>
                            <div className="d-flex flex-wrap gap-2 mb-2">
                                {note.tags.map(tag => (
                                    <Badge key={tag} color="primary" pill className="px-3 py-2 d-inline-flex align-items-center gap-1 shadow-sm">
                                        <span>#{tag}</span>
                                        <X size={14} style={{ cursor: 'pointer' }} onClick={() => removeTag(tag)} />
                                    </Badge>
                                ))}
                            </div>
                            <Input 
                                type="text" 
                                className="border-custom bg-custom rounded-3"
                                value={tagInput}
                                onChange={e => setTagInput(e.target.value)}
                                onKeyDown={handleAddTag}
                                onBlur={commitTag}
                                placeholder={t('tag_input_placeholder')}
                            />
                        </FormGroup>

                        <FormGroup className="mb-4">
                            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
                                <Label className="small fw-bold opacity-75 text-uppercase mb-0">{t('deadline_optional')}</Label>
                                {note.targetDate && (
                                    <div className="form-check form-switch">
                                        <Input 
                                            type="checkbox" 
                                            className="form-check-input cursor-pointer" 
                                            role="switch" 
                                            id="completedSwitch"
                                            checked={note.completed}
                                            onChange={e => setNote({ ...note, completed: e.target.checked })}
                                        />
                                        <Label className="form-check-label small fw-bold text-success d-inline-flex align-items-center gap-1" htmlFor="completedSwitch">
                                            {note.completed ? <><CheckCircle2 size={15} /> {t('completed')}</> : t('mark_as_completed')}
                                        </Label>
                                    </div>
                                )}
                            </div>
                            <DateTimePicker 
                                value={note.targetDate}
                                onChange={e => setNote({ ...note, targetDate: e.target.value })}
                            />
                        </FormGroup>

                        <FormGroup className="mb-4">
                            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                                <Label className="small fw-bold opacity-75 text-uppercase mb-0 d-flex align-items-center gap-1">
                                    <Paperclip size={16} />
                                    <span>{t('attachments')} ({note.attachments ? note.attachments.length : 0})</span>
                                </Label>
                                <div>
                                    <Input type="file" multiple onChange={handleFileChange} className="d-none" id="file-upload" />
                                    <label htmlFor="file-upload" className="btn btn-outline-primary btn-sm rounded-pill px-3 py-2 shadow-sm fw-bold mb-0 d-inline-flex align-items-center gap-2" style={{ cursor: 'pointer' }}>
                                        <Upload size={16} />
                                        <span>{t('add_new_attachments')}</span>
                                    </label>
                                </div>
                            </div>
                            
                            {newFiles.length > 0 && (
                                <ListGroup className="rounded-3 border-0 shadow-sm overflow-hidden mb-3">
                                    {newFiles.map((file, index) => (
                                        <ListGroupItem key={index} className="d-flex flex-wrap justify-content-between align-items-center gap-2 bg-warning bg-opacity-10 border-custom small text-theme py-2 px-3">
                                            <span className="d-inline-flex align-items-center gap-2 text-truncate">
                                                <Upload size={15} className="text-warning flex-shrink-0" />
                                                <span className="fw-semibold text-truncate">{file.name}</span>
                                                <span className="opacity-75">{t('to_upload')}</span>
                                            </span>
                                            <Button
                                                color="outline-danger"
                                                size="sm"
                                                className="rounded-pill px-3 py-1 d-inline-flex align-items-center gap-1 fw-semibold"
                                                onClick={() => removeNewFile(index)}
                                            >
                                                <Trash2 size={14} />
                                                <span>{t('delete')}</span>
                                            </Button>
                                        </ListGroupItem>
                                    ))}
                                </ListGroup>
                            )}

                            {note.attachments && note.attachments.length > 0 ? (
                                <div className="d-flex flex-column gap-2">
                                    {note.attachments.map(att => {
                                        const isPreviewLoading = loadingPreviewId === att.id;
                                        return (
                                            <div
                                                key={att.id}
                                                className="sb-attachment-item d-flex flex-nowrap justify-content-between align-items-center gap-2 py-2 px-3 text-theme"
                                            >
                                                <div
                                                    className="d-flex align-items-center gap-2 flex-grow-1 overflow-hidden"
                                                    style={{ minWidth: 0, cursor: 'pointer' }}
                                                    onClick={() => handlePreview(att.id, att.originalFileName)}
                                                    title={att.originalFileName}
                                                >
                                                    <FileText size={18} className="text-primary flex-shrink-0" />
                                                    <span className="fw-semibold text-truncate">
                                                        {att.originalFileName}
                                                    </span>
                                                </div>
                                                <div className="d-flex flex-nowrap gap-1 gap-sm-2 align-items-center flex-shrink-0">
                                                    <Button 
                                                        color="primary" 
                                                        outline
                                                        size="sm" 
                                                        className="rounded-pill px-2 px-md-3 py-1 fw-semibold d-inline-flex align-items-center justify-content-center gap-1" 
                                                        onClick={() => handlePreview(att.id, att.originalFileName)}
                                                        disabled={loadingPreviewId !== null}
                                                        aria-busy={isPreviewLoading}
                                                        title={t('preview')}
                                                    >
                                                        {isPreviewLoading ? (
                                                            <>
                                                                <Spinner size="sm" style={{ width: '0.85rem', height: '0.85rem', borderWidth: '0.15em' }} />
                                                                <span className="d-none d-md-inline">{t('preview')}...</span>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <Eye size={15} />
                                                                <span className="d-none d-md-inline">{t('preview')}</span>
                                                            </>
                                                        )}
                                                    </Button>
                                                    <Button 
                                                        color="primary" 
                                                        size="sm" 
                                                        className="rounded-pill px-2 px-md-3 py-1 fw-semibold d-inline-flex align-items-center justify-content-center gap-1 shadow-sm" 
                                                        onClick={() => handleDownload(att.id, att.originalFileName)}
                                                        disabled={loadingPreviewId !== null}
                                                        title={t('download')}
                                                    >
                                                        <Download size={15} />
                                                        <span className="d-none d-md-inline">{t('download')}</span>
                                                    </Button>
                                                    <Button 
                                                        color="danger" 
                                                        outline
                                                        size="sm" 
                                                        className="rounded-pill px-2 px-md-3 py-1 d-inline-flex align-items-center justify-content-center gap-1" 
                                                        onClick={() => deleteAttachment(att.id)}
                                                        disabled={loadingPreviewId !== null}
                                                        title={t('delete')}
                                                    >
                                                        <Trash2 size={15} />
                                                        <span className="d-none d-md-inline">{t('delete')}</span>
                                                    </Button>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <div className="text-muted small py-3 px-3 rounded-3 border border-custom bg-custom text-center">
                                    {t('no_attachments')}
                                </div>
                            )}
                        </FormGroup>

                        <div className="d-grid mt-5">
                            <Button color="primary" size="lg" className="rounded-pill fw-bold shadow-sm py-3 d-inline-flex align-items-center justify-content-center gap-2" disabled={saving}>
                                {saving ? (
                                    <>
                                        <Spinner size="sm" />
                                        <span>{t('saving')}...</span>
                                    </>
                                ) : (
                                    <>
                                        <Save size={19} />
                                        <span>{t('save_button')}</span>
                                    </>
                                )}
                            </Button>
                        </div>
                    </Form>
                </CardBody>
            </Card>

            {/* Preview Modal with always-visible Download button & Extracted Text viewer */}
            <Modal isOpen={preview.isOpen} toggle={closePreview} size="xl" centered scrollable contentClassName="bg-card-custom text-theme border-custom">
                <ModalHeader toggle={closePreview} className="border-bottom border-custom py-3 bg-card-custom text-theme">
                    <div className="d-flex flex-nowrap align-items-center justify-content-between gap-2 w-100 pe-2">
                        <div className="d-flex align-items-center gap-2 text-truncate" style={{ minWidth: 0 }}>
                            <FileText size={18} className="text-primary flex-shrink-0" />
                            <span className="small opacity-75 d-none d-sm-inline">{t('file_preview')}:</span>
                            <span className="fw-bold text-truncate">{preview.fileName}</span>
                        </div>
                        <div className="d-flex flex-nowrap align-items-center gap-1 gap-sm-2 flex-shrink-0 ms-auto">
                            {preview.extractedText && (
                                <Button
                                    color={preview.showExtractedText ? "primary" : "outline-primary"}
                                    size="sm"
                                    className="rounded-pill px-2 px-sm-3 py-1 fw-bold shadow-sm d-inline-flex align-items-center justify-content-center gap-1 text-nowrap"
                                    onClick={() => setPreview(prev => ({ ...prev, showExtractedText: !prev.showExtractedText }))}
                                    title={preview.showExtractedText ? t('hide_extracted_text') : t('show_extracted_text')}
                                >
                                    <FileText size={15} className="flex-shrink-0" />
                                    <span className="d-none d-sm-inline">
                                        {preview.showExtractedText ? t('hide_extracted_text') : t('show_extracted_text')}
                                    </span>
                                </Button>
                            )}
                            <Button
                                color="primary"
                                size="sm"
                                className="rounded-pill px-2 px-sm-3 py-1 fw-bold shadow-sm d-inline-flex align-items-center justify-content-center gap-1 text-nowrap"
                                onClick={() => handleDownload(preview.id, preview.fileName, preview.url)}
                                title={t('download')}
                            >
                                <Download size={15} className="flex-shrink-0" />
                                <span className="d-none d-sm-inline">{t('download')}</span>
                            </Button>
                        </div>
                    </div>
                </ModalHeader>
                <ModalBody className="p-4 bg-card-custom">
                    {preview.showExtractedText && preview.extractedText && (
                        <div className="mb-4 p-3 rounded-4 border border-custom bg-custom shadow-sm animate-fade-in">
                            <div className="d-flex flex-nowrap justify-content-between align-items-center gap-2 mb-2 pb-2 border-bottom border-custom">
                                <span className="fw-bold small text-primary d-inline-flex align-items-center gap-1">
                                    <FileText size={15} />
                                    <span>{t('found_in_attachments')}</span>
                                </span>
                                <Button
                                    color={copiedExtracted ? "success" : "primary"}
                                    size="sm"
                                    className="rounded-pill px-3 py-1 fw-bold shadow-sm d-inline-flex align-items-center gap-1"
                                    onClick={handleCopyExtractedText}
                                >
                                    {copiedExtracted ? (
                                        <>
                                            <Check size={15} />
                                            <span>{t('copied')}</span>
                                        </>
                                    ) : (
                                        <>
                                            <Copy size={15} />
                                            <span>{t('copy_text')}</span>
                                        </>
                                    )}
                                </Button>
                            </div>
                            <pre
                                className="mb-0 overflow-auto"
                                style={{
                                    maxHeight: '320px',
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                    fontFamily: 'monospace',
                                    fontSize: '0.88rem',
                                    color: 'var(--sb-text)',
                                    userSelect: 'text'
                                }}
                            >
                                {preview.extractedText}
                            </pre>
                        </div>
                    )}
                    {renderPreviewContent()}
                </ModalBody>
                <ModalFooter className="border-top border-custom bg-card-custom d-flex justify-content-between">
                    <Button
                        color="light"
                        className="rounded-pill px-4 border btn-theme-aware fw-semibold"
                        onClick={closePreview}
                    >
                        {t('cancel')}
                    </Button>
                    <Button
                        color="primary"
                        className="rounded-pill px-4 fw-bold shadow-sm d-inline-flex align-items-center gap-2"
                        onClick={() => handleDownload(preview.id, preview.fileName, preview.url)}
                    >
                        <Download size={16} />
                        <span>{t('download')}</span>
                    </Button>
                </ModalFooter>
            </Modal>

            <style>{`
                .bg-card-custom { background-color: var(--sb-card-bg)!important; }
                .bg-custom { background-color: var(--sb-bg)!important; }
                .border-custom { border-color: var(--sb-border)!important; }
                .text-theme { color: var(--sb-text)!important; }
                .text-muted-custom { color: var(--sb-text-muted)!important; }
                .btn-theme-aware { 
                    background-color: var(--sb-card-bg)!important; 
                    color: var(--sb-text)!important; 
                    border-color: var(--sb-border)!important; 
                }
                .no-hover-card:hover {
                    transform: none !important;
                }
                .modal-title {
                    width: 100%;
                }
                .modal-content { border-radius: 15px; overflow: hidden; }
            `}</style>
        </div>
    );
};

export default EditNote;
